import React from "react";
import { motion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

const HERO_BG =
  "https://images.pexels.com/photos/26050563/pexels-photo-26050563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";
const HERO_IMG_PRIMARY =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_ad-network-hub-3/artifacts/dc8680756f106df5_image.png";
const HERO_IMG_SECONDARY =
  "https://images.pexels.com/photos/35918469/pexels-photo-35918469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";

const ease = [0.22, 1, 0.36, 1];

const Hero = () => {
  const scrollTo = (sel) => {
    const el = document.querySelector(sel);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-ivory pt-28 pb-24 md:pt-36"
    >
      {/* Soft ambient backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 bg-center bg-cover opacity-[0.18]"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 70% 0%, rgba(156,126,31,0.10), transparent 60%), radial-gradient(80% 60% at 0% 100%, rgba(26,40,32,0.08), transparent 60%)",
        }}
      />
      {/* Faint grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(26,40,32,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(26,40,32,0.6) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:pl-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center min-h-[calc(100vh-9rem)]">
          {/* LEFT — Editorial copy */}
          <div className="lg:col-span-6">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease }}
              className="eyebrow flex items-center gap-3 flex-wrap"
              data-testid="hero-eyebrow"
            >
              <span className="w-10 h-px bg-brass" />
              Premium
              <span className="font-serif italic text-brass text-xl md:text-2xl tracking-normal normal-case font-medium px-1">
                Pr DOOH
              </span>
              Network
              <span className="hidden md:inline-block w-1 h-1 bg-brass rounded-full" />
              <span className="hidden md:inline text-forest-500 font-light tracking-[0.25em]">
                EST. 2018
              </span>
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.15 }}
              className="h-display text-[36px] sm:text-5xl lg:text-[64px] xl:text-[76px] mt-7 leading-[1.02]"
              data-testid="hero-title"
            >
              Connect with{" "}
              <span className="italic text-brass">Ultra</span>
              <br />
              <span className="italic text-brass">High Net Worth</span>
              <br />
              audiences in most
              <br />
              <span className="relative inline-block">
                exclusive locations.
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1.2, ease, delay: 1 }}
                  className="absolute -bottom-2 left-0 right-0 h-px bg-brass/50 origin-left"
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.32 }}
              className="mt-10 max-w-xl text-forest-600 text-base md:text-lg font-light leading-relaxed"
              data-testid="hero-subtitle"
            >
              Programmatic digital out-of-home (<span className="font-serif italic text-brass font-medium">Pr DOOH</span>), curated at India's elite golf
              clubs and private members' venues — where considered eyes meet
              considered brands.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.48 }}
              className="mt-12 flex flex-col sm:flex-row gap-4"
            >
              <button
                data-testid="hero-cta-explore"
                onClick={() => scrollTo("#contact")}
                className="btn-gold"
              >
                Explore Opportunities
                <ArrowDownRight size={16} strokeWidth={1.5} />
              </button>
              <button
                data-testid="hero-cta-learn"
                onClick={() => scrollTo("#about")}
                className="btn-ghost"
              >
                Learn More
              </button>
            </motion.div>

            {/* Trust strip */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.7 }}
              className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-4 max-w-xl"
            >
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-brass" />
                <span className="text-[10px] tracking-[0.28em] uppercase text-forest-500">
                  Curated UHNI Reach
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-brass" />
                <span className="text-[10px] tracking-[0.28em] uppercase text-forest-500">
                  Programmatic Buying
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-brass" />
                <span className="text-[10px] tracking-[0.28em] uppercase text-forest-500">
                  Real-Time Analytics
                </span>
              </div>
            </motion.div>
          </div>

          {/* RIGHT — Image collage + floating cards */}
          <div className="lg:col-span-6 relative">
            <div className="relative h-[560px] sm:h-[660px] lg:h-[780px] xl:h-[840px]">
              {/* Primary image — hero, bold presence */}
              <motion.div
                initial={{ opacity: 0, y: 40, rotate: 0 }}
                animate={{ opacity: 1, y: 0, rotate: 1 }}
                transition={{ duration: 1.1, ease, delay: 0.3 }}
                className="absolute inset-0 hairline overflow-hidden shadow-[0_40px_90px_-30px_rgba(26,40,32,0.55)] origin-center"
              >
                <img
                  src={HERO_IMG_PRIMARY}
                  alt="Digital out-of-home placement"
                  className="w-full h-full object-cover bg-ivory-soft"
                />
                <div className="absolute inset-0 ring-2 ring-brass/40" />
              </motion.div>

              {/* Floating badge — top-left of right column */}
              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease, delay: 0.9 }}
                className="absolute -left-2 top-2 hairline bg-ivory/95 backdrop-blur px-4 py-3 z-10"
              >
                <div className="text-[9px] tracking-[0.3em] uppercase text-brass">
                  Network
                </div>
                <div className="font-serif text-2xl text-forest-900 font-light leading-none mt-1">
                  11 <span className="text-brass italic text-lg">venues</span>
                </div>
              </motion.div>

              {/* Floating stat card — bottom-right corner */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease, delay: 1 }}
                className="absolute right-4 bottom-4 hairline bg-ivory/95 backdrop-blur px-5 py-4 z-10 shadow-[0_20px_50px_-20px_rgba(26,40,32,0.3)]"
                data-testid="hero-stat-dwell"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-5xl text-brass font-light leading-none">
                    30+
                  </span>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-forest-500">
                    minutes
                  </span>
                </div>
                <div className="mt-2 text-[11px] tracking-[0.18em] uppercase text-forest-600">
                  Avg. Dwell Time
                </div>
                <div className="mt-3 gold-divider" />
                <div className="mt-3 flex items-center gap-2 text-xs text-forest-500">
                  <ArrowUpRight size={12} strokeWidth={1.6} className="text-brass" />
                  Premium attention, every minute.
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom: scroll indicator + edition tag */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.1 }}
        className="absolute left-6 md:left-20 bottom-8 hidden md:flex items-center gap-3 z-10"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase text-forest-500">
          Scroll to discover
        </span>
        <span className="w-16 h-px bg-forest-500/40 relative overflow-hidden">
          <span className="absolute inset-y-0 left-0 w-1/3 bg-brass animate-pulse" />
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.1 }}
        className="absolute right-6 md:right-12 bottom-8 hidden md:flex items-center gap-2 z-10"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase text-forest-500">
          № 001
        </span>
        <span className="w-px h-8 bg-forest-500/40" />
        <span className="text-[10px] tracking-[0.3em] uppercase text-brass">
          The Considered Edition
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
